import React, { useState, useMemo, useCallback, unstable_useTransition } from 'react'
import { Card, Typography, Button, ButtonGroup, Select, MenuItem } from '@material-ui/core'
import { graphql } from 'babel-plugin-relay/macro'
import { useLazyLoadQuery } from 'react-relay/hooks'
import styled from 'styled-components'
import moment, { Moment } from 'moment'
import { useNavigate, useLocation } from 'react-router'
import CalendarWidgets from './CalendarWidgets'
import ContainerIcon from '../../assets/public/container-icon.png'
import ShipmentIcon from '../../assets/public/shipment-icon.png'
// ============================================================================
// Types & Interfaces
// ============================================================================

interface Props {
  handle: string
  includeArchived: string
}

type CategoryType = 'dc' | 'cc' | 'billing' | 'done' | 'confirmed' | 'release_requested' | 'release_confirmed' | 'pickup_sent' | 'finished'

type ViewType = 'shipment' | 'container'

interface CategoryData {
  count?: number
}

interface CalendarEvent {
  date: string
  categories: {
    dc?: CategoryData
    cc?: CategoryData
    billing?: CategoryData
    done?: CategoryData
    confirmed?: CategoryData
    releaseRequested?: CategoryData
    releaseConfirmed?: CategoryData
    pickupSent?: CategoryData
    finished?: CategoryData
  }
  shipmentTotal: number
  containerTotal: number
}

interface CategoryConfig {
  key: keyof CalendarEvent['categories']
  label?: string
  category: CategoryType
  displayName?: string
}

// ============================================================================
// Constants
// ============================================================================

const PRIMARY_BLUE = '#2196f3'
const PRIMARY_BLUE_DARK = '#1976d2'
const CALENDAR_MAX_WIDTH = 1650
const CALENDAR_DAYS_PER_WEEK = 7
const YEAR_RANGE = 30
const YEAR_OFFSET = 15
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
const TODAY = moment()

// Media query breakpoints
const BREAKPOINT_LARGE = '1660px'
const BREAKPOINT_MEDIUM = '768px'
const BREAKPOINT_SMALL = '480px'

// Shared color values to reduce duplication
const COLOR_ORANGE = { normal: '#fdbf6f', hover: '#e8a85a' }
const COLOR_BLUE = { normal: '#a6cee3', hover: '#8bb8d1' }
const COLOR_GREEN = { normal: '#b2df8a', hover: '#9fce6f' }

// Chart color palette matching ContainerCharts.tsx stagesPieColorsPalette - using lighter shades
const CATEGORY_COLORS: Record<CategoryType, { normal: string; hover: string }> = {
  dc: { normal: '#fb9a99', hover: '#e88a89' }, // Light red/pink
  cc: COLOR_BLUE, // Light blue
  billing: COLOR_GREEN, // Light green
  done: COLOR_ORANGE, // Light orange/tan
  confirmed: COLOR_ORANGE, // Light orange
  release_requested: { normal: '#cab2d6', hover: '#b89cc4' }, // Light purple
  release_confirmed: COLOR_BLUE, // Light blue/teal
  pickup_sent: COLOR_GREEN, // Light green
  finished: COLOR_ORANGE, // Light orange/tan
}

const SHIPMENT_STAGE_MAP: Record<string, string> = {
  dc: 'document_collection',
  cc: 'customs_clearance',
  billing: 'billing',
  done: 'done',
}

const SHIPMENT_CATEGORIES: CategoryConfig[] = [
  { key: 'dc', label: 'DC', category: 'dc' },
  { key: 'cc', label: 'CC', category: 'cc' },
  { key: 'billing', label: 'Ready', category: 'billing' },
  { key: 'done', label: 'Done', category: 'done' },
]

const CONTAINER_CATEGORIES: CategoryConfig[] = [
  { key: 'confirmed', category: 'confirmed', displayName: 'confirmed' },
  { key: 'releaseRequested', category: 'release_requested', displayName: 'release_requested' },
  { key: 'releaseConfirmed', category: 'release_confirmed', displayName: 'release_confirmed' },
  { key: 'pickupSent', category: 'pickup_sent', displayName: 'pickup_sent' },
  { key: 'finished', category: 'finished', displayName: 'finished' },
]

// ============================================================================
// Styled Components
// ============================================================================

const CalendarContainer = styled(Card)`
  padding: 16px;
  margin-bottom: 16px;
  border: none !important;
  box-shadow: none !important;
  font-family: ${(props) => props.theme.typography.fontFamily};
`

const ViewSwitcher = styled(ButtonGroup)`
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  & > button {
    text-transform: none;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 8px 24px;
    border-radius: 0;
    transition: all 0.2s ease-in-out;

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }

    &.MuiButton-contained {
      background-color: ${PRIMARY_BLUE};
      color: white;
      box-shadow: none;

      &:hover {
        background-color: ${PRIMARY_BLUE_DARK};
      }
    }

    &.MuiButton-outlined {
      background-color: ${(props) => props.theme.palette.background.paper};
      color: ${(props) => props.theme.palette.text.primary};
      border-color: ${(props) => props.theme.palette.divider};

      &:hover {
        background-color: ${(props) => props.theme.palette.action.hover};
        border-color: ${(props) => props.theme.palette.divider};
      }
    }
  }
`

const CalendarContentWrapper = styled.div`
  max-width: ${CALENDAR_MAX_WIDTH}px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    padding: 0 8px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    padding: 0 4px;
  }
`

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 4px;
  width: 100%;
  margin-bottom: 16px;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    flex-wrap: wrap;
    gap: 2px;
    margin-bottom: 12px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    margin-bottom: 8px;
    gap: 1px;
  }
`

const MonthButton = styled(Button)<{ $isActive: boolean }>`
  min-width: auto;
  padding: 4px 8px;
  font-size: 0.875rem;
  text-transform: capitalize;
  ${(props) =>
    props.$isActive &&
    `
    background-color: ${PRIMARY_BLUE} !important;
    color: white !important;
  `}
`

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${CALENDAR_DAYS_PER_WEEK}, 1fr);
  gap: 8px;
  width: 100%;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    gap: 4px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    gap: 2px;
  }
`

const DayHeader = styled.div`
  text-align: center;
  padding: 6px 4px;
  font-weight: 600;
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};

  @media (max-width: ${BREAKPOINT_SMALL}) {
    padding: 4px 2px;
    font-size: 0.65rem;
  }
`

const DayCell = styled.div<{ $isCurrentMonth: boolean; $isToday: boolean }>`
  min-height: 130px;
  padding: 4px;
  border: ${(props) => (props.$isToday ? `2px solid ${PRIMARY_BLUE}` : `1px solid ${props.theme.palette.divider}`)};
  border-radius: 4px;
  outline: none;
  background-color: ${(props) => (props.$isCurrentMonth ? props.theme.palette.background.paper : props.theme.palette.action.hover)};
  position: relative;
  display: flex;
  flex-direction: column;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    min-height: 140px;
    padding: 3px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    min-height: 150px;
    padding: 2px;
  }
`

const DayHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  min-height: 30px;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    min-height: 40px;
    align-items: flex-start;
    padding-top: 2px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    min-height: 44px;
    margin-bottom: 2px;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`

const DayNumber = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${(props) => props.theme.palette.text.primary};
  line-height: 1;
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINT_SMALL}) {
    font-size: 0.85rem;
    width: 100%;
  }
`

const DayTotal = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
  font-weight: 500;
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
    font-size: 0.7rem;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 0.65rem;
    width: 100%;
    justify-content: flex-start;
  }
`

const TotalItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`

const TotalIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    width: 14px;
    height: 14px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    width: 12px;
    height: 12px;
  }
`

const CategoryButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  margin-top: 1px;

  @media (max-width: ${BREAKPOINT_SMALL}) {
    gap: 0.5px;
    margin-top: 0.5px;
  }
`

const CategoryButton = styled(Button)<{ $category: CategoryType }>`
  width: calc(33.333% - 0.67px);
  flex-shrink: 0;
  margin: 0;
  padding: 6px 8px;
  font-size: 0.85rem;
  text-transform: none;
  min-width: auto;
  line-height: 1.2;
  border-radius: 5px;
  font-weight: 700;
  height: auto;
  min-height: 16px;
  background-color: ${(props) => {
    const color = CATEGORY_COLORS[props.$category]
    return color?.normal || props.theme.palette.background.default
  }};
  color: white !important;

  &:hover {
    opacity: 0.85;
    background-color: ${(props) => {
      const color = CATEGORY_COLORS[props.$category]
      return color?.hover || props.theme.palette.background.default
    }} !important;
    color: white !important;
  }

  @media (max-width: ${BREAKPOINT_LARGE}) {
    width: calc(50% - 0.5px);
    font-size: 0.8rem;
    padding: 5px 7px;
    min-height: 15px;
  }

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    width: 100%;
    font-size: 0.75rem;
    padding: 4px 6px;
    min-height: 14px;
    margin-bottom: 2px;
  }

  @media (max-width: ${BREAKPOINT_SMALL}) {
    font-size: 0.7rem;
  }
`

// ============================================================================
// Helper Functions
// ============================================================================

const getCategoryCount = (category: CategoryData | null | undefined): number => category?.count || 0

const formatContainerStage = (stage: string): string =>
  stage
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const buildDateFilterParams = (date: string): string => {
  // Format date for filter (same date for start and end to filter single day)
  // Pass both ETA and ETD filters since calendar shows dates based on procedure
  return `voyageEtaStart=${date}&voyageEtaEnd=${date}&voyageEtdStart=${date}&voyageEtdEnd=${date}`
}

const generateCalendarDays = (currentDate: Moment): Moment[] => {
  const days: Moment[] = []
  const firstDay = currentDate.clone().startOf('month')
  const lastDay = currentDate.clone().endOf('month')
  const daysInMonth = currentDate.daysInMonth()
  const startDay = firstDay.day() // 0 = Sunday, 1 = Monday, etc.
  const endDay = lastDay.day() // 0 = Sunday, 6 = Saturday

  // Add previous month's dates to fill the first week
  const daysFromPrevMonth = startDay === 0 ? 6 : startDay - 1
  if (daysFromPrevMonth > 0) {
    const prevMonth = currentDate.clone().subtract(1, 'month')
    const prevMonthBase = prevMonth.startOf('month')
    for (let i = 0; i < daysFromPrevMonth; i++) {
      days.push(prevMonthBase.clone().add(i, 'days'))
    }
  }

  // Add current month's dates - reuse base date
  const monthBase = currentDate.clone().startOf('month')
  for (let i = 0; i < daysInMonth; i++) {
    days.push(monthBase.clone().add(i, 'days'))
  }

  // Add next month's dates only to complete the last week
  const daysNeededToCompleteWeek = endDay === 0 ? 0 : CALENDAR_DAYS_PER_WEEK - endDay
  if (daysNeededToCompleteWeek > 0) {
    const nextMonthBase = currentDate.clone().add(1, 'month').startOf('month')
    for (let i = 0; i < daysNeededToCompleteWeek; i++) {
      days.push(nextMonthBase.clone().add(i, 'days'))
    }
  }

  return days
}

const getShipmentStageFilter = (category: string): string | null => {
  return SHIPMENT_STAGE_MAP[category] || null
}

// Static data - moved outside component to avoid recreation
const MONTHS = moment.months()

// ============================================================================
// Main Component
// ============================================================================

const Calendar: React.FC<Props> = ({ handle, includeArchived }) => {
  const [viewType, setViewType] = useState<ViewType>('shipment')
  const [currentDate, setCurrentDate] = useState(moment())
  const [startTransition] = unstable_useTransition()
  const navigate = useNavigate()
  const location = useLocation()
  const workSpace = useMemo(() => location.pathname.split('/')[1], [location.pathname])

  const year = currentDate.year()
  const month = currentDate.month() + 1

  // Validate handle before making query to prevent invalid queries
  if (!handle || handle.trim() === '') {
    return (
      <CalendarContainer variant='outlined'>
        <Typography variant='body1' color='error'>
          Invalid handle. Cannot load calendar.
        </Typography>
      </CalendarContainer>
    )
  }

  const data = useLazyLoadQuery<any>(
    calendarQuery,
    {
      handle,
      includeArchived,
      viewType,
      year,
      month,
    },
    { fetchPolicy: 'store-or-network' }
  )

  const calendarData = data.calendarQuery

  // Memoized computations
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent>()
    calendarData.calendarEvents.forEach((event: CalendarEvent) => {
      map.set(event.date, event)
    })
    return map
  }, [calendarData.calendarEvents])

  const days = useMemo(() => generateCalendarDays(currentDate), [currentDate])

  const yearOptions = useMemo(() => {
    const currentYear = currentDate.year()
    return Array.from({ length: YEAR_RANGE }, (_, i) => currentYear - YEAR_OFFSET + i)
  }, [currentDate])

  // Event handlers
  const handleCategoryClick = useCallback(
    (category: CategoryType, count: number, date: string) => {
      if (count === 0) return

      const dateParams = buildDateFilterParams(date)
      const basePath = `/${workSpace}/${viewType === 'shipment' ? 'shipments' : 'containers'}`

      if (viewType === 'shipment') {
        const stage = getShipmentStageFilter(category)
        if (stage) {
          navigate(`${basePath}?stage[0]=${stage}&${dateParams}`)
        }
      } else {
        navigate(`${basePath}?stage[0]=${category}&${dateParams}`)
      }
    },
    [viewType, workSpace, navigate]
  )

  const updateDate = useCallback(
    (updater: (prev: Moment) => Moment) => {
      startTransition(() => {
        setCurrentDate(updater)
      })
    },
    [startTransition]
  )

  const handleMonthChange = useCallback((monthIndex: number) => updateDate((prev) => prev.clone().month(monthIndex)), [updateDate])

  const handleYearChange = useCallback((newYear: number) => updateDate((prev) => prev.clone().year(newYear)), [updateDate])

  const handlePrevMonth = useCallback(() => updateDate((prev) => prev.clone().subtract(1, 'month')), [updateDate])

  const handleNextMonth = useCallback(() => updateDate((prev) => prev.clone().add(1, 'month')), [updateDate])

  const handleViewTypeChange = useCallback(
    (newViewType: ViewType) => {
      startTransition(() => {
        setViewType(newViewType)
      })
    },
    [startTransition]
  )

  const renderCategoryButtons = useCallback(
    (event: CalendarEvent) => {
      const categories = viewType === 'shipment' ? SHIPMENT_CATEGORIES : CONTAINER_CATEGORIES

      return (
        <CategoryButtonsContainer>
          {categories
            .filter((config) => getCategoryCount(event.categories[config.key]) > 0)
            .map((config) => {
              const count = getCategoryCount(event.categories[config.key])
              const label = config.label || formatContainerStage(config.displayName || config.category)

              return (
                <CategoryButton
                  key={config.category}
                  $category={config.category}
                  onClick={() => handleCategoryClick(config.category, count, event.date)}
                >
                  {label}: {count}
                </CategoryButton>
              )
            })}
        </CategoryButtonsContainer>
      )
    },
    [viewType, handleCategoryClick]
  )

  // Render helpers
  const renderTotalItem = useCallback(
    (icon: string, alt: string, count: number) => (
      <TotalItem>
        <TotalIcon src={icon} alt={alt} />
        {count}
      </TotalItem>
    ),
    []
  )

  const renderDayCell = useCallback(
    (day: Moment) => {
      const dateKey = day.format('YYYY-MM-DD')
      const event = eventsByDate.get(dateKey)
      const isToday = day.isSame(TODAY, 'day')
      const isCurrentMonth = day.month() === currentDate.month()
      const shipmentCount = event?.shipmentTotal ?? 0
      const containerCount = event?.containerTotal ?? 0
      const hasTotals = shipmentCount > 0 || containerCount > 0

      return (
        <DayCell key={dateKey} $isCurrentMonth={isCurrentMonth} $isToday={isToday}>
          <DayHeaderRow>
            <DayNumber>{day.date()}</DayNumber>
            <DayTotal>
              {hasTotals && (
                <>
                  {containerCount > 0 && renderTotalItem(ContainerIcon, 'Container Icon', containerCount)}
                  {shipmentCount > 0 && renderTotalItem(ShipmentIcon, 'Shipment Icon', shipmentCount)}
                </>
              )}
            </DayTotal>
          </DayHeaderRow>
          {event && renderCategoryButtons(event)}
        </DayCell>
      )
    },
    [eventsByDate, currentDate, renderCategoryButtons, renderTotalItem]
  )

  return (
    <CalendarContainer variant='outlined'>
      <ViewSwitcher>
        <Button variant={viewType === 'shipment' ? 'contained' : 'outlined'} onClick={() => handleViewTypeChange('shipment')}>
          Shipment View
        </Button>
        <Button variant={viewType === 'container' ? 'contained' : 'outlined'} onClick={() => handleViewTypeChange('container')}>
          Container View
        </Button>
      </ViewSwitcher>

      <CalendarContentWrapper>
        <MonthNavigation>
          <Button onClick={handlePrevMonth}>&lt;</Button>
          {MONTHS.map((month, index) => (
            <MonthButton key={month} $isActive={index === currentDate.month()} onClick={() => handleMonthChange(index)}>
              {month}
            </MonthButton>
          ))}
          <Button onClick={handleNextMonth}>&gt;</Button>
          <Select
            value={currentDate.year()}
            onChange={(e) => handleYearChange(e.target.value as number)}
            style={{ marginLeft: 8, minWidth: 80 }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </MonthNavigation>

        <CalendarGrid>
          {DAY_HEADERS.map((day) => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
          {days.map(renderDayCell)}
        </CalendarGrid>
      </CalendarContentWrapper>

      <CalendarWidgets financials={calendarData.financials} categoryChart={calendarData.categoryChart} />
    </CalendarContainer>
  )
}

// ============================================================================
// GraphQL Query
// ============================================================================

const calendarQuery = graphql`
  query CalendarQuery($handle: String!, $includeArchived: String, $viewType: String, $year: Int, $month: Int) {
    calendarQuery(handle: $handle, includeArchived: $includeArchived, viewType: $viewType, year: $year, month: $month) {
      calendarEvents {
        date
        shipmentTotal
        containerTotal
        categories {
          dc {
            count
          }
          cc {
            count
          }
          billing {
            count
          }
          done {
            count
          }
          confirmed {
            count
          }
          releaseRequested {
            count
          }
          releaseConfirmed {
            count
          }
          pickupSent {
            count
          }
          finished {
            count
          }
        }
      }
      financials {
        unpaidCount
        unpaidAmount
      }
      categoryChart {
        month
        categories
      }
    }
  }
`

export default Calendar
